import type { ClientError } from 'graphql-request';
import { useCallback, useEffect, useRef, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { css } from '@emotion/css';
import {
  GetTitleSearchOwnerQuery,
  PropertyTitleOwnerOutput,
  PropertyTitleSearchAddressInput,
  useGetTitleSearchOwnerQuery,
  useStartPropertyTitleSearchMutation,
} from '@graphql/index';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Divider } from '@spark-web/divider';
import { Heading } from '@spark-web/heading';
import { HomeIcon } from '@spark-web/icon';
import { Spinner } from '@spark-web/spinner';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { useQueryClient } from '@tanstack/react-query';
import {
  LAND_TITLE_POLL_INTERVAL_MS,
  LAND_TITLE_POLL_TIMEOUT_MS,
  LAND_TITLE_SEARCH_WAIT_MESSAGES,
  ONE_MINUTE_MS,
} from '../../utils/constants';
import DeclareTitleOwners from './DeclareTitleOwners';
import EligibilityPageHeading from './EligibilityPageHeading';
import EligibilityStepHeader from './EligibilityStepHeader';
import InstallationAddressCard from './InstallationAddressCard';

const TERMINAL_STATUSES = new Set(['PASSED', 'EXPIRED', 'CANCELLED']);

function isTitleSearchAccessDenied(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { response, status } = error as { response?: ClientError['response']; status?: number };
  if (status === 403 || response?.status === 403) return true;
  return (
    response?.errors?.some(({ extensions }) => {
      const originalError = extensions?.originalError as { statusCode?: number } | undefined;
      const nestedResponse = extensions?.response as { status?: number } | undefined;
      return (
        extensions?.code === 'FORBIDDEN' ||
        extensions?.statusCode === 403 ||
        originalError?.statusCode === 403 ||
        nestedResponse?.status === 403
      );
    }) ?? false
  );
}

type PropertyTitleCheckProps = {
  address: string;
  rawAddress: PropertyTitleSearchAddressInput;
  trackingId: string;
  productName: string;
  onContinue: (owners: PropertyTitleOwnerOutput[]) => void;
  onContinueToApplication: () => void;
  declaredOwners: PropertyTitleOwnerOutput[];
  onBack: () => void;
};

function PropertyTitleCheck({
  address,
  rawAddress,
  trackingId,
  productName,
  onContinue,
  onContinueToApplication,
  declaredOwners,
  onBack,
}: PropertyTitleCheckProps) {
  const [searchStarted, setSearchStarted] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const defaultRetry = useQueryClient().getDefaultOptions().queries?.retry;
  const [elapsedMs, setElapsedMs] = useState(0);
  // Wall-clock start of the polling window. Held in a ref so the deadline can be
  // recomputed on demand (see `hasExceededWait`) rather than trusted from a
  // timer that a background tab may have throttled or frozen.
  const startedAtRef = useRef<number | null>(null);

  const { mutate: startSearch } = useStartPropertyTitleSearchMutation({
    onSuccess: () => setSearchStarted(true),
    onError: error => {
      setSearchFailed(true);
      if (isTitleSearchAccessDenied(error)) setAccessDenied(true);
    },
  });

  const hasDeclaredOwners = declaredOwners.length > 0;

  useEffect(() => {
    if (hasDeclaredOwners) return;
    startSearch({ input: { trackingId, address: rawAddress } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Exclude the initial Equifax calls from the wait budget, but bound recovery polling after a failed start too.
  useEffect(() => {
    if ((!searchStarted && !searchFailed) || startedAtRef.current !== null) return;
    startedAtRef.current = Date.now();
    setElapsedMs(0);
  }, [searchStarted, searchFailed]);

  const hasExceededWait = useCallback(
    () => startedAtRef.current !== null && Date.now() - startedAtRef.current >= LAND_TITLE_POLL_TIMEOUT_MS,
    [],
  );

  const { data: titleResult, refetch } = useGetTitleSearchOwnerQuery(
    { input: { applicationId: trackingId } },
    {
      enabled: !accessDenied && (searchStarted || searchFailed),
      select: (data: GetTitleSearchOwnerQuery) => data.getTitleSearchOwner,
      retry: (failureCount, error) => {
        if (isTitleSearchAccessDenied(error)) return false;
        if (typeof defaultRetry === 'function') return defaultRetry(failureCount, error);
        if (typeof defaultRetry === 'boolean') return defaultRetry;
        return failureCount < (defaultRetry ?? 3);
      },
      onError: error => {
        if (isTitleSearchAccessDenied(error)) {
          setAccessDenied(true);
          setSearchFailed(true);
        }
      },
      refetchInterval: (data, query) => {
        if (accessDenied || isTitleSearchAccessDenied(query?.state.error)) return false;
        if (searchFailed && data?.searchStatus === 'NOT_FOUND') return false;
        if (hasExceededWait() && (searchFailed || data?.canManuallyDeclare)) return false;
        if (!data || !TERMINAL_STATUSES.has(data.searchStatus)) return LAND_TITLE_POLL_INTERVAL_MS;
        return false;
      },
      // Without this, react-query suspends refetchInterval whenever the tab is
      // backgrounded. Customers routinely switch tabs while waiting, which left
      // the poll clock stopped while the wait clock kept running — they returned
      // to a timed-out screen for a search that had in fact succeeded.
      refetchIntervalInBackground: true,
    },
  );

  // Timers in a hidden tab are throttled and may be frozen outright, so the
  // elapsed value can be stale on return. Recompute it and poll once immediately
  // whenever the tab becomes visible, rather than waiting for the next interval.
  useEffect(() => {
    if (!searchStarted || accessDenied) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || startedAtRef.current === null) return;
      setElapsedMs(Date.now() - startedAtRef.current);
      if (!hasExceededWait()) void refetch();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [searchStarted, accessDenied, hasExceededWait, refetch]);

  const timedOut = elapsedMs >= LAND_TITLE_POLL_TIMEOUT_MS;

  // Drives the on-screen wait copy only — never the timeout decision. Stops once
  // the wait is over: past that point the fallback UI is showing and there is no
  // message left to advance, so ticking on would re-render every second for as
  // long as the customer sits on that screen.
  useEffect(() => {
    if ((!searchStarted && !searchFailed) || timedOut || accessDenied) return;
    const tick = setInterval(() => {
      if (startedAtRef.current !== null) setElapsedMs(Date.now() - startedAtRef.current);
    }, 1000);
    return () => clearInterval(tick);
  }, [searchStarted, searchFailed, timedOut, accessDenied]);

  // Math.max guards a backwards system-clock step (an NTP correction mid-wait),
  // which would otherwise index the array at -1 and throw on `.title`.
  const waitMessage =
    LAND_TITLE_SEARCH_WAIT_MESSAGES[
      Math.min(Math.max(0, Math.floor(elapsedMs / ONE_MINUTE_MS)), LAND_TITLE_SEARCH_WAIT_MESSAGES.length - 1)
    ];

  const isTerminal = titleResult !== undefined && TERMINAL_STATUSES.has(titleResult.searchStatus);
  const hasOwners = (titleResult?.owners?.length ?? 0) > 0;
  const isPassed = !accessDenied && hasOwners && titleResult?.searchStatus === 'PASSED';
  const manualEntryAvailable = !accessDenied && (hasDeclaredOwners || titleResult?.canManuallyDeclare === true);
  const savedManualOwners = (titleResult?.owners ?? []).filter(owner => owner.isManual) as PropertyTitleOwnerOutput[];
  const manualOwners = hasDeclaredOwners ? declaredOwners : savedManualOwners;
  // Owners already saved against a CANCELLED/EXPIRED search are terminal but not PASSED, so neither
  // isPassed nor the no-owners fallback covers them. Polling has stopped by then, so without this the
  // screen holds the spinner until the wait budget expires. Show the form pre-filled instead of
  // auto-advancing: auto-advancing on manual owners is what broke Back from the income step.
  const showFallback =
    hasDeclaredOwners || savedManualOwners.length > 0 || timedOut || searchFailed || (isTerminal && !hasOwners);

  useEffect(() => {
    if (isPassed && titleResult) {
      onContinue(titleResult.owners as PropertyTitleOwnerOutput[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPassed]);

  useEffect(() => {
    // Owners already on file mean the unavailability was recorded on the first visit; re-logging it
    // when the customer comes back to edit them would double-count the event.
    if (!showFallback || manualOwners.length > 0) return;
    const reason = searchFailed ? 'search_failed' : timedOut ? 'timed_out' : 'no_owners_found';
    datadogRum.addAction('LandOwner Title Check Unavailable', { trackingId, reason });
  }, [showFallback]);

  return (
    <Stack padding={{ tablet: 'xlarge' }} gap="xxlarge">
      <EligibilityStepHeader currentStep={3} totalSteps={5} />

      <EligibilityPageHeading productName={productName}>
        <Heading level="1">
          {showFallback ? "We couldn't find the owners for this address" : 'Checking certificate of title...'}
        </Heading>
      </EligibilityPageHeading>

      <Stack gap="xlarge">
        {showFallback && (
          <Text size="standard">
            {manualEntryAvailable
              ? "This happens sometimes. Enter every owner listed on the property Certificate of Title below, even if they don't live at the property."
              : "Before we can verify your household income, we need to confirm who's the owner of the property on the installation address. We do this by checking the Certificate of title through the land registry."}
          </Text>
        )}

        {showFallback ? (
          <InstallationAddressCard address={address} />
        ) : (
          <Box borderRadius="medium" background="surfaceMuted" borderWidth="standard" border="standard">
            <Box display="flex" alignItems="center" gap="medium" padding="large">
              <Box flexShrink={0}>
                <HomeIcon size="small" tone="primary" />
              </Box>
              <Stack gap="medium">
                <Text size="standard">{address}</Text>
              </Stack>
            </Box>

            <>
              <Divider />
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="medium"
                paddingX="xlarge"
                paddingY="xxxlarge"
              >
                <Box className={css({ transform: 'scale(1.3333)' })}>
                  <Spinner tone="positive" size="xsmall" />
                </Box>
                <Stack gap="large" align="center">
                  <Text size="large" weight="semibold" align="center">
                    {waitMessage.title}
                  </Text>
                  <Text size="small" tone="muted" align="center">
                    {waitMessage.subtitle}
                  </Text>
                </Stack>
              </Box>
            </>
          </Box>
        )}

        {showFallback && (
          <>
            {manualEntryAvailable ? (
              <DeclareTitleOwners
                applicationId={trackingId}
                owners={manualOwners}
                onContinue={onContinue}
                onBack={onBack}
              />
            ) : (
              <>
                <Alert tone="caution" heading="We're unable to retrieve your land title owners.">
                  Our team will be in touch to verify property ownership before your application can be approved.
                </Alert>
                <Stack gap="large">
                  <Button tone="primary" onClick={onContinueToApplication}>
                    Continue to application
                  </Button>
                  <Button prominence="none" tone="neutral" onClick={onBack}>
                    Back
                  </Button>
                </Stack>
              </>
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
}

export default PropertyTitleCheck;
